/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Student Assignment Performance page
 *
 * @file StudentAssignmentPerformance.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Button } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CustomCard from '../common/CustomCard';
import CustomProgressBar from '../common/CustomProgressBar';
import ReviewList from '../common/ReviewList';
import useStudentAssignmentPerformaceStyle from './styles/StudentAssignmentPerformanceStyle';
import HtmlTooltip, { StatusTemplate } from '../common/HtmlTooltip';
import UnderlineButton from '../common/UnderlineTextButton ';
import Tabular from '../tabular/Tabular';


const StudentAssignmentPerformance = ({
  name,
  assignmentName,
  remainingTime,
  firstAttemptCorrect,
  totalQuestions,
  helpTopics,
  topics,
  status,
  tabalurProps,
  config = {},
  onBackButtonClick,
  backButtonText
}) => {
  const classes = useStudentAssignmentPerformaceStyle();

  const renderBackButton = () => {
    if (!config.showBackButton) return null;

    return (
      <Button
        className={classes.backButton}
        startIcon={<ArrowBackIcon />}
        variant="link"
        onClick={config.showBackButton ? onBackButtonClick : null}
      >
        {backButtonText}
      </Button>
    );
  };

  return (
    <div className={classes.root}>
      {renderBackButton()}
      <div className={classes.rowSpaceBetween}>
        <span className={classes.title}>{name}</span>
        <IconButton size="small" aria-label="download">
          <MailOutlineIcon fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.rowSpaceBetween}>
        <div className={classes.assignmentNameContainer}>
          <span className={classes.subtitle2}>{assignmentName}</span>
          {status && (
            <span>
                {status === 'success' && <CheckCircleIcon className={classes.checkIcon} />}
                {status === 'warning' && <ErrorIcon className={classes.errorIcon} />}
                {status === 'error' && <ErrorIcon className={classes.errorIcon} />}
            </span>
          )
          }
        </div>
        <UnderlineButton
          text="Download assignment report"
          onClick={() => {}}
          icon={<CloudDownloadIcon fontSize="small" />}
        />
      </div>
      <div className={classes.wrapperCardContainer}>
        <div className={classes.flexGrow}>
          <CustomCard backgroundColor="#F6F6FB">
            <div className={classes.root}>
              <div>
                <HtmlTooltip
                  title={<StatusTemplate title="25 Questions total" positiveTitle="12 Correct" negativeTitle="5 Wrong" />}
                  arrow
                  placement="bottom"
                >
                  <div style={{ cursor: 'pointer' }}>
                    <CustomProgressBar value={70} bufferValue={20} gap={4} />
                  </div>
                </HtmlTooltip>
              </div>
              <div className={classes.root}>
                <div className={classes.rowSpaceBetween}>
                  <div className={classes.rowSpaceBetween}>
                    <AccessTimeIcon fontSize="small" style={{ alignSelf: 'center' }} />
                    <Typography variant="body2" color="textSecondary">{remainingTime}</Typography>
                  </div>
                  <div>
                    <Typography variant="body2" color="textSecondary">{totalQuestions} questions</Typography>
                  </div>
                </div>
                <div>
                  <Typography variant="body2" color="textSecondary"> {firstAttemptCorrect} out of {totalQuestions} questions answered correctly on first attempt</Typography>
                </div>
                <div>
                  <ReviewList list={helpTopics} />
                </div>
              </div>
            </div>
          </CustomCard>
        </div>
        <div className={classes.flexGrow}>
          <CustomCard backgroundColor="#F6F6FB" style={{ height: '100%' }}>
            <Typography className={classes.topicsCoveredLabel}>Topics needing review</Typography>
            {topics.length > 0 && (
            <Box className={classes.scrollBox}>
              {topics.map((topic, index) => (
                <Typography key={index} variant="body2" color="textSecondary">
                  {topic}
                </Typography>
              ))}
            </Box>
            )}
          </CustomCard>
        </div>
      </div>
      {tabalurProps && (
        <div>
          <Tabular
            {...tabalurProps}
          />
        </div>
      )}
    </div>
  );
};

StudentAssignmentPerformance.propTypes = {
  name: PropTypes.string,
  assignmentName: PropTypes.string,
  remainingTime: PropTypes.string,
  firstAttemptCorrect: PropTypes.number,
  totalQuestions: PropTypes.number,
  helpTopics: PropTypes.array,
  topics: PropTypes.array,
  status: PropTypes.string,
  tabalurProps: PropTypes.shape({
    title: PropTypes.string,
    columns: PropTypes.array,
    data: PropTypes.array
  }),
  config: PropTypes.object,
  onBackButtonClick: PropTypes.func,
  backButtonText: PropTypes.string
};

StudentAssignmentPerformance.defaultProps = {
  name: '',
  assignmentName: '',
  remainingTime: '',
  firstAttemptCorrect: 0,
  totalQuestions: 0,
  helpTopics: [],
  topics: [],
  status: '',
  tabalurProps: null,
  config: {},
  onBackButtonClick: () => {},
  backButtonText: ''
};

export default StudentAssignmentPerformance;
